import { useState, useEffect, useRef, useMemo } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  Search,
  Star,
  Sparkles,
} from "lucide-react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toolsList, ToolId, getToolColorFromCategory } from "@packages/utils";
import { motion, AnimatePresence } from "framer-motion";
import { trackEvent } from "@packages/hooks/useAnalytics";
import { Button, Text, ToolCard } from "@core/index";
import { useAppDispatch, useAppSelector } from "@packages/store/hooks";
import { RootState } from "@packages/store/store";
import { toggleFavoriteTool } from "@packages/store/userSlice";

const NEW_TOOL_IDS = [
  "ai-supported-tasks",
  "blooms-taxonomy-activity",
  "ai-assignment-designer",
  "ai-assessment-scale",
  "transferActivities",
  "ai-assessment-scale",
  "performance-task",
  "essentialQuestions",
  "ai-resistant-homework-idea",
  "heygenVideoGenerator",
  "pronunciation-assessment",
  "visual-matching",
  "spoken-dialogue",
];

const Tools = () => {
  const { t } = useTranslation();
  const { trackEvent: track } = trackEvent();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const category = searchParams.get("category");
  const [activeTab, setActiveTab] = useState(category || "all");
  const [searchTerm, setSearchTerm] = useState("");

  const [filteredTools, setFilteredTools] = useState(toolsList[0]?.tools || []);

  const dispatch = useAppDispatch();
  const favoriteTools = useAppSelector(
    (state: RootState) => state.user.teacher?.preferences?.favoriteTools,
  );

  useEffect(() => {
    if (category) {
      setActiveTab(category);
    } else {
      setActiveTab("all");
    }
  }, [category]);

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);

    const newSearchParams = new URLSearchParams(searchParams);

    if (tab === "all") {
      newSearchParams.delete("category");
    } else {
      newSearchParams.set("category", tab);
    }

    setSearchParams(newSearchParams);
  };

  useEffect(() => {
    let baseFilteredTools = [];
    if (activeTab === "favorites") {
      baseFilteredTools = toolsList.flatMap((category) =>
        category.tools.filter((tool) =>
          favoriteTools?.includes(tool.id as ToolId),
        ),
      );
    } else if (activeTab === "all") {
      baseFilteredTools = toolsList.flatMap((category) => category.tools);
    } else {
      const category = toolsList.find((cat) => cat.category === activeTab);
      baseFilteredTools = category ? category.tools : [];
    }

    if (searchTerm !== "") {
      baseFilteredTools = baseFilteredTools.filter(
        (tool) =>
          t(tool.title).toLowerCase().includes(searchTerm.toLowerCase()) ||
          t(tool.description).toLowerCase().includes(searchTerm.toLowerCase()),
      );
    }

    setFilteredTools(baseFilteredTools);
  }, [searchTerm, activeTab, favoriteTools, t]);

  const handleClickDescription = (toolPath: string) => {
    track(
      {
        category: "ContentDescription",
        action: "tools.description",
        label: "Tools description click",
        nonInteraction: false,
      },
      {
        location: "/tools/description",
        element: "description-button",
      },
    );
    navigate(toolPath);
  };

  const handleClickGeneration = (createPath: string, e: React.MouseEvent) => {
    e.stopPropagation();
    track(
      {
        category: "ContentGeneration",
        action: "tools.generate",
        label: "Tools generation click",
        nonInteraction: false,
      },
      {
        location: "/tools/generate",
        element: "generate-button",
      },
    );
    navigate(createPath);
  };

  const toggleFavorite = async (toolId: ToolId) => {
    try {
      dispatch(toggleFavoriteTool(toolId));
    } catch (error) {
      console.error("Favori araçları güncellerken hata oluştu:", error);
    }
  };

  const tabsContainerRef = useRef<HTMLDivElement>(null);
  const leftButtonRef = useRef<HTMLButtonElement>(null);
  const rightButtonRef = useRef<HTMLButtonElement>(null);

  const scrollAmount = 200;

  const scrollLeft = () => {
    if (tabsContainerRef.current) {
      tabsContainerRef.current.scrollLeft -= scrollAmount;
    }
  };

  const scrollRight = () => {
    if (tabsContainerRef.current) {
      tabsContainerRef.current.scrollLeft += scrollAmount;
    }
  };

  const handleScroll = () => {
    if (!tabsContainerRef.current) return;

    const { scrollLeft, scrollWidth, clientWidth } = tabsContainerRef.current;

    if (leftButtonRef.current) {
      leftButtonRef.current.style.opacity = scrollLeft > 20 ? "1" : "0";
    }

    if (rightButtonRef.current) {
      rightButtonRef.current.style.opacity =
        scrollLeft < scrollWidth - clientWidth - 20 ? "1" : "0";
    }
  };

  useEffect(() => {
    const checkForOverflow = () => {
      if (!tabsContainerRef.current) return;

      const { scrollWidth, clientWidth } = tabsContainerRef.current;
      const hasOverflow = scrollWidth > clientWidth;

      if (rightButtonRef.current) {
        rightButtonRef.current.style.opacity = hasOverflow ? "1" : "0";
      }

      if (leftButtonRef.current) {
        leftButtonRef.current.style.opacity = "0";
      }
    };

    checkForOverflow();

    const resizeObserver = new ResizeObserver(checkForOverflow);

    if (tabsContainerRef.current) {
      resizeObserver.observe(tabsContainerRef.current);
    }

    return () => {
      if (tabsContainerRef.current) {
        resizeObserver.unobserve(tabsContainerRef.current);
      }
      resizeObserver.disconnect();
    };
  }, []);

  const hasMatchingTool = useMemo(
    () =>
      toolsList.some((category) =>
        category.tools.some(
          (tool) =>
            t(tool.title).toLowerCase().includes(searchTerm.toLowerCase()) ||
            t(tool.description)
              .toLowerCase()
              .includes(searchTerm.toLowerCase()),
        ),
      ),
    [searchTerm, t],
  );

  const isNewTool = (toolId: string) => NEW_TOOL_IDS.includes(toolId);

  const renderToolCard = (tool: any) => {
    return (
      <ToolCard
        key={tool.id}
        id={tool.id}
        title={t(tool.title)}
        description={t(tool.description)}
        icon={tool.icon}
        iconBgColor={`bg-gradient-to-br ${tool.colors?.light || "from-gray-500 to-gray-600"}`}
        onClick={() => handleClickDescription(tool.path)}
        onToggleFavorite={(e) => {
          e.stopPropagation();
          toggleFavorite(tool.id as ToolId);
        }}
        isFavorite={Boolean(favoriteTools?.includes(tool.id))}
        isNew={isNewTool(tool.id)}
      />
    );
  };

  return (
    <div className="flex-1 md:p-8 p-4 lg:mt-5 bg-gray-50 dark:bg-dark-500 lg:rounded-tl-3xl overflow-y-auto h-full custom-scrollbar">
      <div className="max-w-7xl mx-auto">
        {/* Search and Tabs Section */}
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-8">
          {/* Tabs */}
          <div className="relative w-full lg:w-auto">
            <div className="relative flex items-center">
              <button
                ref={leftButtonRef}
                onClick={scrollLeft}
                className="absolute left-0 z-10 flex items-center justify-center w-8 h-8 bg-white dark:bg-gray-800 rounded-full shadow-md border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none opacity-0 transition-opacity duration-200"
                aria-label="Scroll left"
              >
                <ChevronLeft className="w-5 h-5 text-gray-600 dark:text-gray-300" />
              </button>

              <div
                ref={tabsContainerRef}
                className="w-full overflow-x-auto scrollbar-hide py-2 scroll-smooth max-w-[calc(100vw_-_45px)] lg:max-w-none"
                onScroll={handleScroll}
              >
                <div className="inline-flex items-center gap-2">
                  <Button
                    size="md"
                    rounded="lg"
                    bgColor={
                      activeTab === "all"
                        ? "bg-dark-500 dark:bg-dark-500"
                        : "bg-white dark:bg-dark-600 hover:bg-gray-50 dark:hover:bg-dark-500"
                    }
                    textColor={
                      activeTab === "all"
                        ? "text-white dark:text-white"
                        : "text-gray-700 dark:text-gray-300"
                    }
                    className={`${
                      activeTab === "all"
                        ? "border-dark-400 dark:border-dark-400"
                        : "border-gray-300 dark:border-dark-400"
                    }`}
                    onClick={() => handleTabChange("all")}
                  >
                    {t("common.all")}
                  </Button>

                  <Button
                    size="md"
                    rounded="lg"
                    bgColor={
                      activeTab === "favorites"
                        ? "bg-dark-500 dark:bg-dark-500"
                        : "bg-white dark:bg-dark-600 hover:bg-gray-50 dark:hover:bg-dark-500"
                    }
                    textColor={
                      activeTab === "favorites"
                        ? "text-white dark:text-white"
                        : "text-gray-700 dark:text-gray-300"
                    }
                    className={`${
                      activeTab === "favorites"
                        ? "border-dark-400 dark:border-dark-400"
                        : "border-gray-300 dark:border-dark-400"
                    }`}
                    onClick={() => handleTabChange("favorites")}
                  >
                    <Star className="w-4 h-4 mr-2" />
                    {t("common.favorites")}
                  </Button>

                  {toolsList.map((category) => (
                    <Button
                      key={category.category}
                      size="md"
                      rounded="lg"
                      bgColor={
                        activeTab === category.category
                          ? "bg-dark-500 dark:bg-dark-500"
                          : "bg-white dark:bg-dark-600 hover:bg-gray-50 dark:hover:bg-dark-500"
                      }
                      textColor={
                        activeTab === category.category
                          ? "text-white dark:text-white"
                          : "text-gray-700 dark:text-gray-300"
                      }
                      className={`${
                        activeTab === category.category
                          ? "border-dark-400 dark:border-dark-400"
                          : "border-gray-300 dark:border-dark-400"
                      }`}
                      onClick={() => handleTabChange(category.category)}
                    >
                      {t(category.categoryTitle)}
                    </Button>
                  ))}
                </div>
              </div>

              <button
                ref={rightButtonRef}
                onClick={scrollRight}
                className="absolute right-0 z-10 flex items-center justify-center w-8 h-8 bg-white dark:bg-gray-800 rounded-full shadow-md border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none opacity-0 transition-opacity duration-200"
                aria-label="Scroll right"
              >
                <ChevronRight className="w-5 h-5 text-gray-600 dark:text-gray-300" />
              </button>
            </div>
          </div>

          {/* Search */}
          <div className="relative w-full lg:w-64">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <Search className="h-4 w-4 text-gray-400" />
            </div>
            <input
              type="text"
              className="block w-full pl-10 pr-3 py-2.5 border border-gray-200 dark:border-dark-400 rounded-full bg-white dark:bg-dark-600 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200"
              placeholder={t("common.search")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {/* Tools Grid */}
        <div className="space-y-8">
          {activeTab === "all" ? (
            // Show categories in the "all" tab
            toolsList.map((category) => {
              const filteredCategoryTools = category.tools
                .filter((tool) => tool.id !== "essay-grader")
                .filter((tool) =>
                  searchTerm
                    ? t(tool.title)
                        .toLowerCase()
                        .includes(searchTerm.toLowerCase()) ||
                      t(tool.description)
                        .toLowerCase()
                        .includes(searchTerm.toLowerCase())
                    : true,
                );

              // Only render category if it has matching tools
              if (filteredCategoryTools.length === 0) {
                return null;
              }

              return (
                <section key={category.category} className="space-y-6">
                  <div className="flex items-center gap-4">
                    <Text variant="h2">{t(category.categoryTitle)}</Text>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    <AnimatePresence mode="popLayout">
                      {filteredCategoryTools.map((tool) =>
                        renderToolCard(tool),
                      )}
                    </AnimatePresence>
                  </div>
                </section>
              );
            })
          ) : (
            // Show filtered tools for other tabs
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <AnimatePresence mode="popLayout">
                {filteredTools
                  .filter((tool) => tool.id !== "essay-grader")
                  .filter((tool) =>
                    searchTerm
                      ? t(tool.title)
                          .toLowerCase()
                          .includes(searchTerm.toLowerCase()) ||
                        t(tool.description)
                          .toLowerCase()
                          .includes(searchTerm.toLowerCase())
                      : true,
                  )
                  .map((tool) => renderToolCard(tool))}
              </AnimatePresence>
            </div>
          )}

          {/* Empty State for No Results */}
          {((activeTab === "all" && !hasMatchingTool) ||
            (activeTab !== "all" && filteredTools.length === 0)) && (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="text-center">
                <Text variant="h2" className="mb-2">
                  {activeTab === "favorites"
                    ? t("common.noFavorites")
                    : t("common.noSearchResults")}
                </Text>
                <Text variant="p" status="secondary">
                  {activeTab === "favorites"
                    ? t("common.addFavoritesHint")
                    : t("common.tryDifferentSearch")}
                </Text>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Tools;
